import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, db } from '../firebase/firebaseConfig';
import { doc, getDoc, updateDoc, increment } from 'firebase/firestore';
import { customToast } from '../utils/toastUtils';
import './AtsScorePage.css';
import { useAuthGuard } from '../hooks/useAuthGuard';

const AtsScorePage = ({ uploadedFile, useCache = true, onBack }) => {
  // 🔒 AUTH GUARD: Redirect non-logged users to home with login modal
  const { user: authUser, loading: authLoading } = useAuthGuard();
  
  const navigate = useNavigate();
  const [score, setScore] = useState(0);
  const [displayScore, setDisplayScore] = useState(0); // For animated counter
  const [isAnalyzing, setIsAnalyzing] = useState(true);
  const [analysisData, setAnalysisData] = useState(null);
  const [userPlan, setUserPlan] = useState(null); // Store user's plan info
  const [isLoadingPlan, setIsLoadingPlan] = useState(true);

  const isAnalyzingRef = useRef(false); // Lock to prevent concurrent/duplicate analysis calls

  // Fetch user plan from Firestore
  useEffect(() => {
    const fetchUserPlan = async () => {
      try {
        const user = auth.currentUser;
        if (user) {
          const userDocRef = doc(db, 'users', user.uid);
          const userDoc = await getDoc(userDocRef);
          
          if (userDoc.exists()) {
            const userData = userDoc.data();
            const planData = {
              plan: userData.planType || 'free', // 'free', 'silver', 'gold', 'platinum'
              isActive: userData.isActivePlan || false // Use isActivePlan from Firestore
            };

            setUserPlan(planData);
          } else {
            // No user document, default to free
            setUserPlan({ plan: 'free', isActive: false });
          }
        } else {
          // Not logged in, treat as non-user (free)
          setUserPlan({ plan: 'free', isActive: false });
        }
      } catch (error) {
        console.error('❌ Error fetching user plan:', error);
        setUserPlan({ plan: 'free', isActive: false });
      } finally {
        setIsLoadingPlan(false);
      }
    };

    fetchUserPlan();
  }, []);

  // Determine what features are unlocked based on plan
  const getFeatureAccess = () => {
    if (!userPlan) {
      return {
        scoreAvailable: true,
        resumeHighlightsUnlocked: false,
        skillGapUnlocked: false
      };
    }

    const planType = userPlan.plan.toLowerCase();
    const isActive = userPlan.isActive;

    let access = {};

    switch (planType) {
      case 'silver':
        access = {
          scoreAvailable: true,
          resumeHighlightsUnlocked: isActive, // Silver unlocks Resume Highlights only
          skillGapUnlocked: false              // Silver does NOT unlock Skill Gap
        };
        break;
      case 'gold':
        access = {
          scoreAvailable: true,
          resumeHighlightsUnlocked: isActive, // Gold unlocks both
          skillGapUnlocked: isActive          // Gold unlocks Skill Gap
        };
        break;
      case 'platinum':
        access = {
          scoreAvailable: true,
          resumeHighlightsUnlocked: isActive, // Platinum unlocks both
          skillGapUnlocked: isActive          // Platinum unlocks Skill Gap
        };
        break;
      case 'free':
      default:
        access = {
          scoreAvailable: true,
          resumeHighlightsUnlocked: false,    // Free has nothing unlocked
          skillGapUnlocked: false
        };
        break;
    }

    return access;
  };

  const featureAccess = getFeatureAccess();

  // Animate score counter like CIBIL score
  useEffect(() => {
    if (score > 0 && !isAnalyzing) {
      let currentScore = 0;
      const increment = score / 60; // 60 steps for smooth animation
      const timer = setInterval(() => {
        currentScore += increment;
        if (currentScore >= score) {
          setDisplayScore(score);
          clearInterval(timer);
        } else {
          setDisplayScore(Math.floor(currentScore));
        }
      }, 20); // Update every 20ms for smooth animation
      
      return () => clearInterval(timer);
    }
  }, [score, isAnalyzing]);

  useEffect(() => {
    // Analyze resume when file is uploaded
    // Guard: prevent React StrictMode / double-mount from firing analysis twice
    if (uploadedFile && !isAnalyzingRef.current) {
      analyzeResume();
    }
  }, [uploadedFile]);

  // Update ATS usage count in Firestore (single atomic increment)
  const updateAtsUsageCount = async () => {
    try {
      const user = auth.currentUser;
      if (!user) return;

      const userDocRef = doc(db, 'users', user.uid);
      
      // Increment atsChecksUsed by 1
      await updateDoc(userDocRef, {
        atsChecksUsed: increment(1),
        lastAtsCheckAt: new Date().toISOString()
      });

    } catch (error) {
      console.error('❌ Failed to update ATS usage count:', error);
      // Don't throw error - this shouldn't stop the analysis flow
    }
  };

  /**
   * Check ATS limit against the plans collection
   * Returns { allowed: boolean, used: number, limit: number|string }
   */
  const checkAtsLimit = async (userId) => {
    const userDocRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userDocRef);

    if (!userDoc.exists()) {
      return { allowed: false, used: 0, limit: 0 };
    }

    const userData = userDoc.data();
    const atsChecksUsed = userData.atsChecksUsed || 0;
    const planType = (userData.planType || 'free').toLowerCase();
    const isActive = userData.isActivePlan || false;

    // Free / inactive plan → use the user-level atsCheckLimit (default 5)
    if (planType === 'free' || !isActive) {
      const localLimit = userData.atsCheckLimit ?? 5;
      const unlimited = localLimit === -1;
      return {
        allowed: unlimited || atsChecksUsed < localLimit,
        used: atsChecksUsed,
        limit: unlimited ? 'Unlimited' : localLimit
      };
    }

    // Active paid plan → fetch authoritative limit from plans collection
    try {
      const planRef = doc(db, 'plans', planType);
      const planSnap = await getDoc(planRef);

      if (planSnap.exists()) {
        const planData = planSnap.data();
        const atsCheckAllowed = planData.atsCheckAllowed ?? planData.atsCheckLimit ?? 5;
        const unlimited = atsCheckAllowed === -1;
        return {
          allowed: unlimited || atsChecksUsed < atsCheckAllowed,
          used: atsChecksUsed,
          limit: unlimited ? 'Unlimited' : atsCheckAllowed
        };
      }
    } catch (err) {
      console.error('⚠️ Failed to fetch plan from plans collection:', err);
    }

    // Fallback to user-level limit
    const fallbackLimit = userData.atsCheckLimit ?? 5;
    return {
      allowed: fallbackLimit === -1 || atsChecksUsed < fallbackLimit,
      used: atsChecksUsed,
      limit: fallbackLimit === -1 ? 'Unlimited' : fallbackLimit
    };
  };

  const analyzeResume = async () => {
    // 🔒 Prevent duplicate calls from React StrictMode / double-mount
    if (isAnalyzingRef.current) return;
    isAnalyzingRef.current = true;
    setIsAnalyzing(true);
    
    try {
      // Get current user ID
      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error('Please login to analyze your resume');
      }
      
      // ═══ CHECK ATS LIMIT FROM PLANS COLLECTION ═══
      const limitCheck = await checkAtsLimit(currentUser.uid);
      
      if (!limitCheck.allowed) {
        setIsAnalyzing(false);
        isAnalyzingRef.current = false;
        customToast.error(`ATS score check limit reached (${limitCheck.used}/${limitCheck.limit}). Please upgrade your plan.`);
        return; // Stop analysis
      }
      
      // Create FormData to send file
      const formData = new FormData();
      formData.append('resume', uploadedFile);
      formData.append('userId', currentUser.uid); // Add userId for plan validation

      // Get backend URL from environment variable
      const backendUrl = import.meta.env.VITE_BACKEND_URL || 'http://localhost:5000';

      // Call backend API with bypassCache parameter (opposite of useCache)
      const url = `${backendUrl}/api/ats/analyze?bypassCache=${!useCache}`;

      // Helper: POST with one automatic retry on transient (503) errors.
      // The backend already retries inside OpenAI calls; this is a client-side
      // safety net for cases where the entire request itself fails / 503s.
      const postWithRetry = async (attempt = 1) => {
        const resp = await fetch(url, { method: 'POST', body: formData });
        if (resp.ok) return resp;

        const errPayload = await resp.json().catch(() => ({}));
        const retryable = resp.status === 503 || errPayload.retryable === true;

        if (retryable && attempt < 2) {
          console.warn(`⚠️  Transient backend error (status ${resp.status}). Retrying in 2s...`);
          await new Promise((r) => setTimeout(r, 2000));
          return postWithRetry(attempt + 1);
        }

        const err = new Error(errPayload.error || `API error: ${resp.statusText}`);
        err.retryable = retryable;
        err.status = resp.status;
        throw err;
      };

      const response = await postWithRetry();

      const result = await response.json();
      
      if (!result.success) {
        throw new Error(result.error || 'Analysis failed');
      }

      // Map backend response to frontend format
      const analysisResult = result.analysis;
      const mappedData = {
        score: analysisResult.atsScore || 0,
        rating: analysisResult.rating || 'N/A',
        fileName: result.fileName || uploadedFile.name,
        fileSize: `${(result.fileSize / 1024).toFixed(2)} KB`,
        analyzedAt: new Date(result.analyzedAt).toLocaleString(),
        strengths: analysisResult.strengths || [],
        weaknesses: analysisResult.weaknesses || [],
        improvements: analysisResult.improvements || [],
        skillGaps: analysisResult.skillGaps || { technical: [], soft: [], industrySpecific: [] },
        keywords: {
          found: analysisResult.keywordOptimization?.present || [],
          missing: analysisResult.keywordOptimization?.missing || []
        },
        summary: analysisResult.summary || '',
        tokensUsed: result.tokensUsed,
        cached: result.cached || false,
        cacheMessage: result.cacheMessage || ''
      };

      setScore(analysisResult.atsScore);
      setAnalysisData(mappedData);
      
      // Update Firestore: Increment atsChecksUsed counter (once only)
      await updateAtsUsageCount();
      
    } catch (error) {
      console.error('❌ Analysis error:', error);
      
      // Show error to user via toast
      const { customToast } = await import('../utils/toastUtils');
      customToast.error(error.message || 'Failed to analyze resume. Please try again.');
      
      // Fallback to show some basic info
      setAnalysisData({
        score: 0,
        fileName: uploadedFile.name,
        fileSize: `${(uploadedFile.size / 1024).toFixed(2)} KB`,
        analyzedAt: new Date().toLocaleString(),
        error: error.message
      });
    } finally {
      setIsAnalyzing(false);
      // NOTE: intentionally NOT resetting isAnalyzingRef here.
      // This prevents StrictMode re-mount from calling analyzeResume again.
      // The ref resets naturally when the component is truly unmounted and user navigates back.
    }
  };

  const getScoreColor = (score) => {
    if (score >= 90) return '#10b981'; // green - Excellent
    if (score >= 75) return '#f59e0b'; // yellow - Good
    if (score >= 51) return '#f97316'; // orange - Needs Improvement
    return '#ef4444'; // red - Poor
  };

  const getScoreLabel = (score) => {
    if (score >= 90) return 'Excellent';
    if (score >= 75) return 'Good';
    if (score >= 51) return 'Needs Improvement';
    return 'Poor';
  };

  const getSectionIcon = (sectionName) => {
    const icons = {
      contact: (
        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" width="24" height="24">
          <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.89 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" fill="currentColor"/>
        </svg>
      ),
      summary: (
        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" width="24" height="24">
          <path d="M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z" fill="currentColor"/>
        </svg>
      ),
      experience: (
        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" width="24" height="24">
          <path d="M20 6h-4V4c0-1.11-.89-2-2-2h-4c-1.11 0-2 .89-2 2v2H4c-1.11 0-1.99.89-1.99 2L2 19c0 1.11.89 2 2 2h16c1.11 0 2-.89 2-2V8c0-1.11-.89-2-2-2zm-6 0h-4V4h4v2z" fill="currentColor"/>
        </svg>
      ),
      education: (
        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" width="24" height="24">
          <path d="M5 13.18v4L12 21l7-3.82v-4L12 17l-7-3.82zM12 3L1 9l11 6 9-4.91V17h2V9L12 3z" fill="currentColor"/>
        </svg>
      ),
      skills: (
        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" width="24" height="24">
          <path d="M9 11H7v2h2v-2zm4 0h-2v2h2v-2zm4 0h-2v2h2v-2zm2-7h-1V2h-2v2H8V2H6v2H5c-1.11 0-1.99.9-1.99 2L3 20c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 16H5V9h14v11z" fill="currentColor"/>
        </svg>
      )
    };
    return icons[sectionName] || icons.summary;
  };
  
  const handleEditResume = () => {
    navigate('/templates');
  };

  const handleCheckPremium = () => {
    navigate('/pricing');
  };

  if (isAnalyzing) {
    return (
      <div className="ats-score-page ats-analyzing-view">
        <div className="analyzing-container">
          <div className="analyzing-animation">
            <div className="scanner-line"></div>
            <div className="document-icon">
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" width="64" height="64">
                <path d="M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z" fill="#1A8063"/>
              </svg>
            </div>
          </div>
          <h2>Analyzing Your Resume...</h2>
          <p>Our AI is scanning your resume for ATS compatibility</p>
          <div className="progress-steps">
            <div className="step active">
              <span className="step-icon">
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" width="20" height="20">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" fill="#10b981"/>
                </svg>
              </span>
              <span>Parsing Document</span>
            </div>
            <div className="step active">
              <span className="step-icon">
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" width="20" height="20" className="spinning">
                  <path d="M12 4V1L8 5l4 4V6c3.31 0 6 2.69 6 6 0 1.01-.25 1.97-.7 2.8l1.46 1.46C19.54 15.03 20 13.57 20 12c0-4.42-3.58-8-8-8zm0 14c-3.31 0-6-2.69-6-6 0-1.01.25-1.97.7-2.8L5.24 7.74C4.46 8.97 4 10.43 4 12c0 4.42 3.58 8 8 8v3l4-4-4-4v3z" fill="#f59e0b"/>
                </svg>
              </span>
              <span>Analyzing Keywords</span>
            </div>
            <div className="step">
              <span className="step-icon">
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" width="20" height="20">
                  <circle cx="12" cy="12" r="10" stroke="#94a3b8" strokeWidth="2" fill="none"/>
                </svg>
              </span>
              <span>Generating Report</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // 🔒 Show loading state while checking authentication
  if (authLoading) {
    return (
      <div className="ats-score-page" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <div style={{ textAlign: 'center' }}>
          <div className="spinner" style={{ margin: '0 auto 1rem' }}></div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  // 🔒 Don't render if not authenticated (will redirect via hook)
  if (!authUser) {
    return null;
  }

  return (
    <div className="ats-score-page">
      {/* Header */}
      <div className="ats-page-header">
        <button 
          className="back-to-home-btn"
          onClick={() => navigate('/')}
          aria-label="Back to Home"
        >
          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z" fill="currentColor"/>
          </svg>
          Back to Home
        </button>
        <h1>ATS Score Report</h1>
      </div>

      {/* Main Score Display */}
      <div className="score-hero-section">
        <div className="speedometer-container">
          <svg className="speedometer" viewBox="0 0 400 260" xmlns="http://www.w3.org/2000/svg">
            <defs>
              {/* Gradient Definitions */}
              <linearGradient id="redGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" style={{ stopColor: '#dc2626', stopOpacity: 1 }} />
                <stop offset="100%" style={{ stopColor: '#ef4444', stopOpacity: 1 }} />
              </linearGradient>
              <linearGradient id="orangeGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" style={{ stopColor: '#ea580c', stopOpacity: 1 }} />
                <stop offset="100%" style={{ stopColor: '#f97316', stopOpacity: 1 }} />
              </linearGradient>
              <linearGradient id="yellowGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" style={{ stopColor: '#f59e0b', stopOpacity: 1 }} />
                <stop offset="100%" style={{ stopColor: '#fbbf24', stopOpacity: 1 }} />
              </linearGradient>
              <linearGradient id="greenGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" style={{ stopColor: '#059669', stopOpacity: 1 }} />
                <stop offset="100%" style={{ stopColor: '#10b981', stopOpacity: 1 }} />
              </linearGradient>
            </defs>
            
            {/* Background Track - Semicircle */}
            <path
              d="M 50 210 A 150 150 0 0 1 350 210"
              fill="none"
              stroke="#e5e7eb"
              strokeWidth="32"
              strokeLinecap="round"
            />
            
            {/* Red Zone (0-50 "Poor"): 0% to 50% of arc */}
            <path
              d="M 50 210 A 150 150 0 0 1 350 210"
              fill="none"
              stroke="url(#redGradient)"
              strokeWidth="32"
              strokeLinecap="round"
              strokeDasharray="235.62 471.24"
              style={{ transition: 'all 0.3s ease' }}
            />
            
            {/* Orange Zone (51-74 "Needs Improvement"): 50% to 74% */}
            <path
              d="M 50 210 A 150 150 0 0 1 350 210"
              fill="none"
              stroke="url(#orangeGradient)"
              strokeWidth="32"
              strokeLinecap="round"
              strokeDasharray="113.1 471.24"
              strokeDashoffset="-235.62"
              style={{ transition: 'all 0.3s ease' }}
            />
            
            {/* Yellow Zone (75-89 "Good"): 74% to 89% */}
            <path
              d="M 50 210 A 150 150 0 0 1 350 210"
              fill="none"
              stroke="url(#yellowGradient)"
              strokeWidth="32"
              strokeLinecap="round"
              strokeDasharray="70.69 471.24"
              strokeDashoffset="-348.72"
              style={{ transition: 'all 0.3s ease' }}
            />
            
            {/* Green Zone (90-100 "Excellent"): 89% to 100% */}
            <path
              d="M 50 210 A 150 150 0 0 1 350 210"
              fill="none"
              stroke="url(#greenGradient)"
              strokeWidth="32"
              strokeLinecap="round"
              strokeDasharray="51.84 471.24"
              strokeDashoffset="-419.41"
              style={{ transition: 'all 0.3s ease' }}
            />
            
            
            
            
            {/* Score Labels - REMOVED */}
            
            {/* Needle - Animated with correct rotation */}
            {/* Formula: -90° (start) + (score * 180 / 100) */}
            <g 
              transform={`rotate(${-90 + (displayScore * 180 / 100)} 200 210)`} 
              style={{ transition: 'transform 2s cubic-bezier(0.34, 1.56, 0.64, 1)' }}
            >
              {/* Needle Shadow */}
              <line
                x1="200"
                y1="210"
                x2="200"
                y2="80"
                stroke="rgba(0,0,0,0.15)"
                strokeWidth="6"
                strokeLinecap="round"
                transform="translate(3, 3)"
              />
              {/* Needle Triangle Shape */}
              <polygon
                points="200,85 195,200 200,210 205,200"
                fill={getScoreColor(score)}
                stroke={getScoreColor(score)}
                strokeWidth="2"
                strokeLinejoin="round"
              />
              {/* Needle Highlight */}
              <line
                x1="200"
                y1="210"
                x2="200"
                y2="85"
                stroke="rgba(255,255,255,0.3)"
                strokeWidth="1.5"
                strokeLinecap="round"
                transform="translate(-1, 0)"
              />
            </g>
            
            {/* Center Hub */}
            <circle cx="200" cy="210" r="20" fill={getScoreColor(score)} opacity="0.2" />
            <circle cx="200" cy="210" r="16" fill={getScoreColor(score)} />
            <circle cx="200" cy="210" r="11" fill="white" opacity="0.95" />
            <circle cx="200" cy="210" r="6" fill={getScoreColor(score)} />
            <circle cx="198" cy="208" r="2" fill="rgba(255,255,255,0.8)" />
          </svg>
          
          <div className="score-display">
            <div className="score-number">{displayScore}</div>
          </div>
        </div>
        
        <div className="score-details">
          <h2 className="score-label" style={{ color: getScoreColor(score), fontSize: '2.8rem' }}>
            {getScoreLabel(score)}
          </h2>
          <p className="score-description">
            {score >= 90
              ? 'Your resume has excellent ATS compatibility and is well-optimized for applicant tracking systems.'
              : score >= 75
                ? 'Your resume has good ATS compatibility. A few improvements can boost your score further.'
                : score >= 51
                  ? 'Your resume needs improvement to pass ATS filters effectively. Review the suggestions below.'
                  : 'Your resume may struggle to pass ATS filters. Significant improvements are recommended.'}
          </p>
          
          <div className="file-info">
            <div className="file-info-item">
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" width="18" height="18" className="info-icon">
                <path d="M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z" fill="#64748b"/>
              </svg>
              <span>{analysisData?.fileName}</span>
            </div>
            <div className="file-info-item">
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" width="18" height="18" className="info-icon">
                <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zM9 17H7v-7h2v7zm4 0h-2V7h2v10zm4 0h-2v-4h2v4z" fill="#64748b"/>
              </svg>
              <span>{analysisData?.fileSize}</span>
            </div>
            <div className="file-info-item">
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" width="18" height="18" className="info-icon">
                <path d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67z" fill="#64748b"/>
              </svg>
              <span>{analysisData?.analyzedAt}</span>
            </div>
          </div>
        </div>
      </div>

      {/* AI Summary Section */}
      {analysisData?.summary && (
        <div className="ai-summary-section">
          <div className="summary-header">
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" width="24" height="24">
              <path d="M9 2C7.89 2 7 2.9 7 4v2c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h10c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2V4c0-1.1-.89-2-2-2H9zm0 2h6v2H9V4zm-2 6h10v10H7V10zm2 2v2h6v-2H9zm0 3v2h6v-2H9z" fill="#1A8063"/>
            </svg>
            <h3>AI Analysis Summary</h3>
          </div>
          <p className="summary-text">{analysisData.summary}</p>
        </div>
      )}

      {/* Features Section - Resume Highlights & Skill Gap Side by Side */}
      <div className="features-container">
        {/* Resume Highlights Section - Conditionally Locked */}
        {!featureAccess.resumeHighlightsUnlocked ? (
          <div className="compact-locked-section">
            {/* Blurred Background Preview */}
            <div className="locked-preview-bg">
              <div className="preview-card">
                <div className="preview-header"></div>
                <div className="preview-line"></div>
                <div className="preview-line short"></div>
                <div className="preview-line"></div>
              </div>
              <div className="preview-card">
                <div className="preview-header"></div>
                <div className="preview-line"></div>
                <div className="preview-line short"></div>
                <div className="preview-line"></div>
              </div>
              <div className="preview-card">
                <div className="preview-header"></div>
                <div className="preview-line"></div>
                <div className="preview-line short"></div>
                <div className="preview-line"></div>
              </div>
            </div>
            
            <div className="compact-lock-content">
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" width="32" height="32">
                <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z" fill="#1A8063"/>
              </svg>
              <div className="compact-lock-text">
                <h4>Resume Highlights</h4>
                <p>Upgrade to Silver, Gold, or Platinum to unlock insights</p>
              </div>
              <button 
                className="btn-compact-upgrade" 
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  navigate('/pricing');
                }}
              >
                Upgrade
              </button>
            </div>
          </div>
        ) : (
          <div className="resume-highlights-section">
            <h3>
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" width="24" height="24">
                <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" fill="#1A8063"/>
              </svg>
              Resume Highlights
            </h3>
            <div className="highlights-grid">
              <div className="highlight-card strength-card">
                <div className="highlight-header">
                  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" width="28" height="28">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" fill="#10b981"/>
                  </svg>
                  <h4>Strengths</h4>
                </div>
                <ul className="highlight-list">
                  {analysisData?.strengths?.length > 0 ? (
                    analysisData.strengths.map((strength, index) => (
                      <li key={index}>{strength}</li>
                    ))
                  ) : (
                    <li>Analyzing your resume strengths...</li>
                  )}
                </ul>
              </div>

              <div className="highlight-card weakness-card">
                <div className="highlight-header">
                  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" width="28" height="28">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" fill="#f59e0b"/>
                  </svg>
                  <h4>Weaknesses</h4>
                </div>
                <ul className="highlight-list">
                  {analysisData?.weaknesses?.length > 0 ? (
                    analysisData.weaknesses.map((weakness, index) => (
                      <li key={index}>{weakness}</li>
                    ))
                  ) : (
                    <li>Analyzing areas for improvement...</li>
                  )}
                </ul>
              </div>

              <div className="highlight-card improvement-card">
                <div className="highlight-header">
                  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" width="28" height="28">
                    <path d="M16 6l2.29 2.29-4.88 4.88-4-4L2 16.59 3.41 18l6-6 4 4 6.3-6.29L22 12V6z" fill="#3b82f6"/>
                  </svg>
                  <h4>Areas for Improvements</h4>
                </div>
                <ul className="highlight-list">
                  {analysisData?.improvements?.length > 0 ? (
                    analysisData.improvements.map((improvement, index) => (
                      <li key={index}>{improvement}</li>
                    ))
                  ) : (
                    <li>Identifying improvement opportunities...</li>
                  )}
                </ul>
              </div>
            </div>
          </div>
        )}

      {/* Skill Gap Analysis Section - Conditionally Locked */}
      {!featureAccess.skillGapUnlocked ? (
        <div className="compact-locked-section">
          {/* Blurred Background Preview */}
          <div className="locked-preview-bg">
            <div className="preview-card">
              <div className="preview-header"></div>
              <div className="preview-line"></div>
              <div className="preview-line short"></div>
              <div className="preview-line"></div>
            </div>
            <div className="preview-card">
              <div className="preview-header"></div>
              <div className="preview-line"></div>
              <div className="preview-line short"></div>
              <div className="preview-line"></div>
            </div>
          </div>
          
          <div className="compact-lock-content">
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" width="32" height="32">
              <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z" fill="#1A8063"/>
            </svg>
            <div className="compact-lock-text">
              <h4>Skill Gap Analysis</h4>
              <p>Upgrade to Gold or Platinum to discover skill gaps</p>
            </div>
            <button 
              className="btn-compact-upgrade" 
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                navigate('/pricing');
              }}
            >
              Upgrade
            </button>
          </div>
        </div>
      ) : (
        <div className="skill-gap-section">
        <h3>
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" width="24" height="24">
                <path d="M9.4 16.6L4.8 12l4.6-4.6L8 6l-6 6 6 6 1.4-1.4zm5.2 0l4.6-4.6-4.6-4.6L16 6l6 6-6 6-1.4-1.4z" fill="#1A8063"/>
              </svg>
              Skill Gap Analysis
            </h3>
            <div className={`skill-gap-content ${!featureAccess.skillGapUnlocked ? 'blurred' : ''}`}>
              {(() => {
                // ─── Local helpers scoped to the Skill Gap section only ───
                const normalizeImportance = (value) => {
                  if (!value) return 'medium';
                  const v = value.toString().trim().toLowerCase();
                  if (v.startsWith('high') || ['critical','urgent','priority-high','very high','high priority'].includes(v)) return 'high';
                  if (v.startsWith('low')  || ['minor','priority-low','low priority'].includes(v)) return 'low';
                  return 'medium';
                };

                const categoryIcons = {
                  technical: (
                    <svg viewBox="0 0 24 24" fill="none" width="20" height="20" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                      <path d="M8 5H6a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h2M16 5h2a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2h-2M10 9h4M10 12h4M10 15h4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
                    </svg>
                  ),
                  soft: (
                    <svg viewBox="0 0 24 24" fill="none" width="20" height="20" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                      <circle cx="9" cy="8" r="3" stroke="currentColor" strokeWidth="1.8"/>
                      <circle cx="17" cy="9" r="2.4" stroke="currentColor" strokeWidth="1.8"/>
                      <path d="M3 19c0-3 2.5-5 6-5s6 2 6 5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
                      <path d="M14.5 19c.2-2 2-3.7 4.5-3.7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
                    </svg>
                  ),
                  industry: (
                    <svg viewBox="0 0 24 24" fill="none" width="20" height="20" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                      <path d="M3 21V9l9-5 9 5v12M9 21v-9h6v9" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" strokeLinecap="round"/>
                    </svg>
                  ),
                  found: (
                    <svg viewBox="0 0 24 24" fill="none" width="20" height="20" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                      <path d="M20 6 9 17l-5-5" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  ),
                  recommend: (
                    <svg viewBox="0 0 24 24" fill="none" width="20" height="20" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                      <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round"/>
                    </svg>
                  ),
                };

                const renderSkillCard = (item, idx) => {
                  const priority = normalizeImportance(item.importance);
                  return (
                    <div key={idx} className={`sg-skill-card priority-${priority}`}>
                      <div className="sg-skill-card-top">
                        <h5 className="sg-skill-card-title">{item.skill}</h5>
                        <span className={`sg-priority-pill ${priority}`}>
                          <span className="sg-priority-dot" aria-hidden="true"></span>
                          {priority}
                        </span>
                      </div>
                      {item.reason && <p className="sg-skill-card-reason">{item.reason}</p>}
                    </div>
                  );
                };

                const renderCategory = (title, iconKey, items, hint) => {
                  if (!items?.length) return null;
                  return (
                    <section className={`sg-category sg-category--${iconKey}`}>
                      <header className="sg-category-header">
                        <div className="sg-category-title">
                          <span className="sg-category-icon">{categoryIcons[iconKey]}</span>
                          <h4>{title}</h4>
                        </div>
                        <span className="sg-count-chip">{items.length}</span>
                      </header>
                      {hint && <p className="sg-category-hint">{hint}</p>}
                      <div className="sg-card-grid">
                        {items.map(renderSkillCard)}
                      </div>
                    </section>
                  );
                };

                const renderKeywordCategory = (title, iconKey, items, variant, hint) => {
                  if (!items?.length) return null;
                  return (
                    <section className={`sg-category sg-category--${iconKey}`}>
                      <header className="sg-category-header">
                        <div className="sg-category-title">
                          <span className="sg-category-icon">{categoryIcons[iconKey]}</span>
                          <h4>{title}</h4>
                        </div>
                        <span className="sg-count-chip">{items.length}</span>
                      </header>
                      {hint && <p className="sg-category-hint">{hint}</p>}
                      <div className="sg-keyword-cloud">
                        {items.map((kw, i) => (
                          <span key={i} className={`sg-keyword sg-keyword--${variant}`}>
                            {variant === 'recommend' && <span className="sg-keyword-prefix" aria-hidden="true">+</span>}
                            {kw}
                          </span>
                        ))}
                      </div>
                    </section>
                  );
                };

                const sg = analysisData?.skillGaps || {};
                const kw = analysisData?.keywords || {};
                const hasAnything =
                  (sg.technical?.length || 0) +
                  (sg.soft?.length || 0) +
                  (sg.industrySpecific?.length || 0) +
                  (kw.found?.length || 0) +
                  (kw.missing?.length || 0) > 0;

                if (!analysisData || !analysisData.skillGaps) {
                  return (
                    <div className="loading-state">
                      <p>Analyzing skill gaps and keywords...</p>
                    </div>
                  );
                }

                return (
                  <>
                    {renderCategory('Technical Skills', 'technical', sg.technical)}
                    {renderCategory('Soft Skills', 'soft', sg.soft)}
                    {renderCategory('Industry-Specific Skills', 'industry', sg.industrySpecific)}
                    {renderKeywordCategory('Keywords Found in Resume', 'found', kw.found, 'found')}
                    {renderKeywordCategory(
                      'Recommended Keywords to Add',
                      'recommend',
                      kw.missing,
                      'recommend',
                      'Sprinkle these high-impact keywords into your resume to boost ATS match.'
                    )}
                    {!hasAnything && (
                      <div className="loading-state">
                        <p>No skill gaps detected — great work!</p>
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          </div>
        )}
      </div> {/* End of features-container */}

      {/* Action Buttons */}
      <div className="action-buttons">
        <button className="btn-primary" onClick={handleEditResume}>
          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" width="20" height="20" style={{marginRight: '8px'}}>
            <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z" fill="white"/>
          </svg>
          Enhance Your Resume
        </button>
        
        {/* Show Check Premium button only for free users */}
        {userPlan?.plan?.toLowerCase() === 'free' && (
          <button className="btn-premium" onClick={handleCheckPremium}>
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" width="20" height="20" style={{marginRight: '8px'}}>
              <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" fill="white"/>
            </svg>
            Check Premium
          </button>
        )}
        
        {/* Show Upgrade Plan button only for silver users (gold and platinum have full access) */}
        {userPlan?.plan?.toLowerCase() === 'silver' && (
          <button className="btn-premium" onClick={handleCheckPremium}>
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" width="20" height="20" style={{marginRight: '8px'}}>
              <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" fill="white"/>
            </svg>
            Upgrade/Renew Plan
          </button>
        )}
        
        {/* Gold and Platinum users see no second button - they have full access */}
      </div>


    </div>
  );
};

export default AtsScorePage;

